/** Languages a storefront can offer. Keep in sync with the API (`shared/locale.ts`) and the storefront. */
export const STORE_LANGUAGES = [
  { code: 'en', label: 'English', flag: '🇺🇸' },
  { code: 'es', label: 'Español (Spanish)', flag: '🇪🇸' },
  { code: 'de', label: 'Deutsch (German)', flag: '🇩🇪' },
  { code: 'fr', label: 'Français (French)', flag: '🇫🇷' },
] as const

export type StoreLanguageCode = (typeof STORE_LANGUAGES)[number]['code']

export function isStoreLanguageCode(v: unknown): v is StoreLanguageCode {
  return STORE_LANGUAGES.some((l) => l.code === v)
}

/** Coerce API / MySQL JSON (array or stringified array) into a string list. */
function asSupportedList(supported: unknown): string[] {
  if (Array.isArray(supported)) return supported.map(String)
  if (typeof supported === 'string' && supported.trim()) {
    try {
      const parsed: unknown = JSON.parse(supported)
      return Array.isArray(parsed) ? parsed.map(String) : []
    } catch {
      return []
    }
  }
  return []
}

/** Supported languages as saved on the store: always includes the default, in canonical order. */
export function normalizeLanguageChoice(
  defaultCode: string | null | undefined,
  supported: readonly string[] | string | null | undefined,
): { defaultLocale: StoreLanguageCode; supportedLocales: StoreLanguageCode[] } {
  const defaultLocale = isStoreLanguageCode(defaultCode) ? defaultCode : 'en'
  const set = new Set<string>([defaultLocale, ...asSupportedList(supported)])
  return {
    defaultLocale,
    supportedLocales: STORE_LANGUAGES.map((l) => l.code).filter((c) => set.has(c)),
  }
}
